import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { POLLING_INTERVAL_MS } from "../../constants/polling-constants";
import { queryKeys } from "../../lib/queryKeys";
import { getOpcdCodingResults } from "../../services";
import type { ProFeeCodingResultsResponse } from "../../types/pro-fee-coding-results";
import { normalizeProFeeCodingResultsResponse } from "../../utils/pro-fee-coding-results";
import { useOpcdEncounterLookups } from "./useOpcdEncounterLookups";

const fetchOpcdCodingResults = async (params: {
  providerNpi: string;
  mrn: string;
  dateOfService?: string;
}): Promise<ProFeeCodingResultsResponse> => {
  try {
    const response = await getOpcdCodingResults(params);
    return normalizeProFeeCodingResultsResponse(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return normalizeProFeeCodingResultsResponse(error.response?.data);
    }
    throw error;
  }
};

export const useOpcdCodingResults = (options?: {
  polling?: boolean;
  enabled?: boolean;
}) => {
  const { providerNpi, mrn, dateOfService } = useOpcdEncounterLookups();
  const enabled =
    (options?.enabled ?? true) && Boolean(providerNpi && mrn);

  return useQuery({
    queryKey: queryKeys.opcdCodingResults(
      providerNpi,
      mrn,
      dateOfService ?? ""
    ),
    queryFn: () =>
      fetchOpcdCodingResults({
        providerNpi,
        mrn,
        dateOfService,
      }),
    enabled,
    refetchInterval: options?.polling ? POLLING_INTERVAL_MS : false,
    retry: 0,
  });
};
